import { useApolloClient } from '@apollo/client';
import { Button, message } from 'antd';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import styled from 'styled-components';
import {
    type RevokeSessionMutation,
    type RevokeSessionMutationVariables,
    RevokeSessionDocument,
} from '../api/mutations/revokeSession';
import SignOutIcon from '../assets/ci/menu/signout.svg';
import { type ThemeNotificationApi } from '../themes/makeNotificationApi';
import { useAccountContext, useSessionID } from './contexts/AccountContextManager';

const Container = styled.div`
    color: #000;
    display: flex;
    align-items: center;

    svg {
        fill: #000;
        margin: 0 10px;
    }
`;

const SignOutButton = ({ themedNotification }: { themedNotification?: ThemeNotificationApi }) => {
    const { t } = useTranslation('common');
    const navigate = useNavigate();
    const { logout } = useAccountContext();

    const sessionId = useSessionID();
    const apolloClient = useApolloClient();
    const notification = themedNotification || message;
    const handleSignOut = useCallback(
        (event: React.MouseEvent<HTMLButtonElement>) => {
            event.stopPropagation();
            event.preventDefault();

            apolloClient
                .mutate<RevokeSessionMutation, RevokeSessionMutationVariables>({
                    mutation: RevokeSessionDocument,
                    variables: { sessionId, displayNotice: false },
                })
                .then(() => {
                    // first unset the session
                    logout();

                    // then go to sign in page
                    navigate('/auth/signIn', { replace: true, state: { noWebAuthn: true } });

                    // and finally display a message
                    notification.success({
                        content: t('common:messages.successLogout'),
                        key: 'primary',
                    });
                });

            return false;
        },
        [apolloClient, sessionId, logout, navigate, notification, t]
    );

    return (
        <Button onClick={handleSignOut} type="link">
            <Container>
                <SignOutIcon />
                {t('common:leftMenu.signout')}
            </Container>
        </Button>
    );
};

export default SignOutButton;
